import { useState } from "react"
import { useTranslation } from "react-i18next"
import { DownloadSimple } from "@phosphor-icons/react"
import { format } from "date-fns"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker"
import { useToast } from "@/hooks/useToast"
import { downloadSofiaFile } from "@/lib/download-file-helper"

export const ExportAssignmentHistory = () => {
  const user = useUser()

  const { t } = useTranslation()
  const { toast } = useToast()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [isDownloading, setIsDownloading] = useState(false)

  if (!user.isOneOf(["MedicalSupervisor"])) {
    return null
  }

  const handleDownload = async () => {
    if (!selectedDate) {
      return
    }

    const isoDate = format(selectedDate, "yyyy-MM-dd")

    setIsDownloading(true)
    await downloadSofiaFile({
      endpoint: `api/export/appointment-assignment-history?date=${isoDate}`,
      fileName: `appointment-assignment-history-${isoDate}.xlsx`,
      onComplete: () => toast({ description: t("admin.advanced.exportAssignmentHistory.success") }),
      onError: () => toast({ description: t("admin.advanced.exportAssignmentHistory.error") }),
    })
    setIsDownloading(false)
  }

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardHeaderTitle className="flex items-center gap-4">
          {t("admin.advanced.exportAssignmentHistory.title")}
        </CardHeaderTitle>
        <div className="flex items-center gap-4">
          <DatePicker
            label={t("admin.advanced.exportAssignmentHistory.dateLabel")}
            value={selectedDate}
            onSelect={(date) => setSelectedDate(date)}
            onReset={() => setSelectedDate(undefined)}
          />
          <Button
            variant="tertiary"
            className="p-2"
            aria-label={t("admin.advanced.exportAssignmentHistory.download")}
            isLoading={isDownloading}
            disabled={!selectedDate || isDownloading}
            onClick={handleDownload}>
            <DownloadSimple className="text-on-surface-subdued" />
          </Button>
        </div>
      </CardHeader>
    </Card>
  )
}
